import { useApolloClient, useQuery } from '@apollo/client/react';
import { Bell } from 'lucide-react';
import { useCallback, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import Empty from '@/components/blocks/Empty';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuItem,
} from '@/components/common/Dropdown/Elements';
import NotificationItem from '@/pages/organizationAdmin/Notifications/NotificationItem';
import {
    FetchNotificationsQuery,
    FetchUnreadNotificationsCountQuery,
    MarkAllNotificationsAsReadMutation,
} from '@/pages/organizationAdmin/Notifications/api.graphql';
import useNewNotificationSync from '@/pages/organizationAdmin/Notifications/useNewNotificationSync';
import { subscribeToNotificationRefetch } from '@/utils/notificationPubSub';

const NotificationMessage = () => {
    const { t } = useTranslation('notifications');
    const dropdownId = useId();

    const { data, refetch } = useQuery(FetchNotificationsQuery, {
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: 0, limit: 5 },
        },
    });

    const { data: unreadData, refetch: refetchUnread } = useQuery(FetchUnreadNotificationsCountQuery, {
        fetchPolicy: 'cache-and-network',
    });

    const hasNotifications = !!data?.lists.items.length;

    const apolloClient = useApolloClient();
    const markAllAsRead = useCallback(async () => {
        const mutation = await apolloClient.mutate({
            mutation: MarkAllNotificationsAsReadMutation,
        });

        if (mutation.data?.result) {
            refetch();
            refetchUnread();
        }
    }, [apolloClient, refetch, refetchUnread]);

    const onRefetch = useCallback(() => {
        refetch();
        refetchUnread();
    }, [refetch, refetchUnread]);

    // Subscribe to notification refetch events from pubsub
    useEffect(() => {
        const unsubscribe = subscribeToNotificationRefetch(onRefetch);
        return unsubscribe;
    }, [onRefetch]);

    const emittedAt = useNewNotificationSync();
    useEffect(() => {
        onRefetch();
    }, [onRefetch, emittedAt]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-cy="notificationAction"
                asChild
                className="cursor-pointer"
                data-cy-id={dropdownId}
            >
                <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-default-100 dark:hover:bg-default-200 data-[state=open]:bg-default-100 dark:data-[state=open]:bg-default-200 hover:text-primary text-default-500 dark:text-default-800 relative h-8 w-8 rounded-full md:h-9 md:w-9"
                >
                    <Bell className="h-6 w-6" />
                    {!!unreadData?.unreadNotificationsCount && (
                        <Badge
                            text={unreadData?.unreadNotificationsCount || 0}
                            className="ring-primary-foreground absolute bottom-[calc(100%-16px)] left-[calc(100%-18px)] h-4 w-4 items-center justify-center p-0 text-xs font-medium ring-2"
                        />
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="p-0" align="end" data-cy-id={dropdownId}>
                <DropdownMenuLabel className="flex h-full w-full items-center justify-between p-4">
                    <span className="text-base font-semibold">{t('notifications:title')}</span>
                    {hasNotifications && (
                        <button
                            onClick={markAllAsRead}
                            className="hover:decoration-default-100 dark:decoration-default-900 cursor-pointer text-xs font-medium"
                        >
                            {t('notifications:markAllAsRead')}
                        </button>
                    )}
                </DropdownMenuLabel>
                <div className="min-w-76">
                    {(data?.lists.items || []).map((item, index) => (
                        <NotificationItem isFromDropdown key={`inbox-${index}`} {...item} refetch={onRefetch} />
                    ))}
                    {data?.lists.items.length === 0 && (
                        <div className="flex w-full justify-center p-10">
                            <Empty title={t('notifications:empty')} />
                        </div>
                    )}
                </div>
                {hasNotifications && (
                    <>
                        <DropdownMenuSeparator />
                        <Link to="/notifications">
                            <DropdownMenuItem className="m-2">
                                <Button className="w-full">{t('notifications:viewAll')}</Button>
                            </DropdownMenuItem>
                        </Link>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationMessage;
